import { generatePath } from 'react-router-dom';

import { useAuth } from '@gbs-monorepo-packages/auth';
import { BaseTooltip, getRouteFrom } from '@gbs-monorepo-packages/common';

import { TEMPLATE_VIEW } from '../../../../constants/RoutePaths';
import { type ITemplatePageDTO } from '../../../../services/templates';
import { useCanTemplate } from '../Can';
import { TextWithTooltip } from '../TextWithTooltip';
import {
  ActionButton,
  DeleteIcon,
  EditIcon,
  PreviewIcon,
  PreviewLink,
  TemplateCardActionContainer,
  TemplateCardContainer,
  TemplateCardIcon,
  TemplateCardIconContainer,
  TemplateCardName,
  TemplateCardNameContainer,
  TemplateCardTitleContainer,
} from './styles';

export interface ITemplateCardProps {
  templateData: ITemplatePageDTO;
  showIcon?: boolean;
  iconSize?: 'normal' | 'small';

  onEditClick?: (template: ITemplatePageDTO) => void;
  onDeleteClick?: (template: ITemplatePageDTO) => void;
}

export const TemplateCard = ({
  templateData,
  showIcon = true,
  iconSize = 'normal',
  onEditClick,
  onDeleteClick,
}: ITemplateCardProps): JSX.Element => {
  const { user } = useAuth();

  const allowEditAndDelete = useCanTemplate(templateData, user);

  return (
    <TemplateCardContainer
      data-cy={`template-card-${templateData.id}-container`}
    >
      <TemplateCardTitleContainer data-cy="template-card-title-container">
        {showIcon && (
          <TemplateCardIconContainer data-cy="template-card-icon-container">
            <TemplateCardIcon
              data-cy={`template-card-${templateData.id}-icon`}
            />
          </TemplateCardIconContainer>
        )}

        <TemplateCardNameContainer data-cy="template-card-name-container">
          <TextWithTooltip
            text={templateData.title}
            dataCy={`template-${templateData.id}-name`}
          >
            <TemplateCardName />
          </TextWithTooltip>
        </TemplateCardNameContainer>
      </TemplateCardTitleContainer>

      <TemplateCardActionContainer data-cy="template-card-action-container">
        <BaseTooltip message="Preview">
          <PreviewLink
            data-cy="button-previewLink"
            target="_blank"
            to={generatePath(getRouteFrom(TEMPLATE_VIEW), {
              templateId: templateData.id.toString(),
            })}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <PreviewIcon iconsize={iconSize} />
          </PreviewLink>
        </BaseTooltip>
        {onEditClick && (
          <BaseTooltip message="Edit">
            <ActionButton
              onClick={(e) => {
                e.stopPropagation();

                onEditClick(templateData);
              }}
              data-cy="button-edit"
              isVisible={allowEditAndDelete}
            >
              <EditIcon iconsize={iconSize} />
            </ActionButton>
          </BaseTooltip>
        )}
        {onDeleteClick && (
          <BaseTooltip message="Delete">
            <ActionButton
              onClick={(e) => {
                e.stopPropagation();

                onDeleteClick(templateData);
              }}
              data-cy="button-delete"
              isVisible={allowEditAndDelete}
            >
              <DeleteIcon iconsize={iconSize} />
            </ActionButton>
          </BaseTooltip>
        )}
      </TemplateCardActionContainer>
    </TemplateCardContainer>
  );
};
